import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  Users, 
  FileText, 
  Briefcase, 
  AlertTriangle,
  CheckCircle,
  Clock,
  Download
} from 'lucide-react';

const AdminAnalytics = ({ reports, users, jobs }) => {
  const [selectedTimeRange, setSelectedTimeRange] = useState('30d');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate loading analytics data
    const loadAnalytics = () => {
      setIsLoading(true);
      
      // Calculate analytics from the provided data
      const analytics = calculateAnalytics(reports, users, jobs, selectedTimeRange);
      
      setTimeout(() => {
        setAnalyticsData(analytics);
        setIsLoading(false);
      }, 1000);
    };

    loadAnalytics();
  }, [reports, users, jobs, selectedTimeRange]);

  const calculateAnalytics = (reports, users, jobs, timeRange) => {
    const now = new Date();
    const daysAgo = timeRange === '7d' ? 7 : timeRange === '30d' ? 30 : 90;
    const cutoffDate = new Date(now.getTime() - (daysAgo * 24 * 60 * 60 * 1000));

    // Filter data based on time range
    const recentReports = reports.filter(r => new Date(r.created_at) >= cutoffDate);
    const recentUsers = users.filter(u => new Date(u.created_at) >= cutoffDate);
    const recentJobs = jobs.filter(j => new Date(j.created_at) >= cutoffDate);

    // Calculate metrics
    const totalUsers = users.length;
    const activeUsers = users.filter(u => u.moderation_status?.status !== 'suspended').length;
    const suspendedUsers = users.filter(u => u.moderation_status?.status === 'suspended').length;
    
    const totalReports = reports.length;
    const pendingReports = reports.filter(r => r.status === 'submitted').length;
    const resolvedReports = reports.filter(r => r.status === 'resolved').length;
    const dismissedReports = reports.filter(r => r.status === 'dismissed').length;
    
    const totalJobs = jobs.length;
    const activeJobs = jobs.filter(j => j.status === 'active').length;
    const flaggedJobs = jobs.filter(j => j.status === 'flagged').length;
    
    // Calculate trends (mock data for demonstration)
    const userGrowthRate = Math.round(((recentUsers.length / totalUsers) * 100) || 0);
    const reportResolutionRate = Math.round(((resolvedReports / totalReports) * 100) || 0);
    const jobApprovalRate = Math.round(((activeJobs / totalJobs) * 100) || 0);

    return {
      overview: {
        totalUsers,
        activeUsers,
        suspendedUsers,
        totalReports,
        pendingReports,
        resolvedReports,
        dismissedReports,
        totalJobs,
        activeJobs,
        flaggedJobs
      },
      trends: {
        userGrowthRate,
        reportResolutionRate,
        jobApprovalRate,
        recentActivity: {
          reports: recentReports.length,
          users: recentUsers.length,
          jobs: recentJobs.length
        }
      },
      charts: {
        reportsByStatus: [
          { name: 'Pending', value: pendingReports, color: '#f59e0b' },
          { name: 'Resolved', value: resolvedReports, color: '#10b981' },
          { name: 'Dismissed', value: dismissedReports, color: '#6b7280' }
        ],
        usersByStatus: [
          { name: 'Active', value: activeUsers, color: '#10b981' },
          { name: 'Suspended', value: suspendedUsers, color: '#ef4444' }
        ],
        jobsByStatus: [
          { name: 'Active', value: activeJobs, color: '#10b981' },
          { name: 'Flagged', value: flaggedJobs, color: '#ef4444' }
        ]
      }
    };
  };

  const MetricCard = ({ title, value, trend, icon: IconComponent }) => (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {trend !== undefined && (
            <div className="flex items-center mt-2">
              <TrendingUp className={`w-4 h-4 ${trend >= 0 ? 'text-green-500' : 'text-red-500'}`} />
              <span className={`text-sm ml-1 ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {trend >= 0 ? '+' : ''}{trend}%
              </span>
              <span className="text-sm text-gray-500 ml-1">vs last period</span>
            </div>
          )}
        </div>
        <div className="p-3 rounded-lg bg-blue-50">
          <IconComponent className="w-6 h-6 text-blue-600" />
        </div>
      </div>
    </div>
  );

  const ChartCard = ({ title, data }) => (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      <div className="space-y-3">
        {data.map((item, index) => (
          <div key={index} className="flex items-center justify-between">
            <div className="flex items-center">
              <div 
                className="w-3 h-3 rounded-full mr-3" 
                style={{ backgroundColor: item.color }}
              ></div>
              <span className="text-sm text-gray-600">{item.name}</span>
            </div>
            <div className="flex items-center">
              <span className="text-sm font-medium text-gray-900">{item.value}</span>
              <div className="w-16 ml-3">
                <div className="bg-gray-200 rounded-full h-2">
                  <div 
                    className="h-2 rounded-full" 
                    style={{ 
                      backgroundColor: item.color, 
                      width: `${(item.value / Math.max(...data.map(d => d.value))) * 100}%` 
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Analytics Dashboard</h2>
          <div className="h-9 w-32 bg-gray-200 rounded animate-pulse"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm border p-6">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-8 bg-gray-200 rounded w-1/2 mb-3"></div>
                <div className="h-3 bg-gray-200 rounded w-full"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Analytics Dashboard</h2>
          <p className="text-sm text-gray-600 mt-1">
            Platform insights and moderation metrics
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={selectedTimeRange}
            onChange={(e) => setSelectedTimeRange(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
          <button className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50">
            <Download className="w-4 h-4" />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Total Users"
          value={analyticsData.overview.totalUsers.toLocaleString()}
          trend={analyticsData.trends.userGrowthRate}
          icon={Users}
          color="blue"
        />
        <MetricCard
          title="Active Reports"
          value={analyticsData.overview.pendingReports}
          trend={-5}
          icon={FileText}
          color="yellow"
        />
        <MetricCard
          title="Total Jobs"
          value={analyticsData.overview.totalJobs.toLocaleString()}
          trend={12}
          icon={Briefcase}
          color="green"
        />
        <MetricCard
          title="Flagged Content"
          value={analyticsData.overview.flaggedJobs + analyticsData.overview.suspendedUsers}
          trend={-8}
          icon={AlertTriangle}
          color="red"
        />
      </div>

      {/* Detailed Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard
          title="Reports by Status"
          data={analyticsData.charts.reportsByStatus}
        />
        <ChartCard
          title="Users by Status"
          data={analyticsData.charts.usersByStatus}
        />
      </div>

      {/* Activity Summary */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Recent Activity Summary</h3>
          <p className="text-sm text-gray-600 mt-1">Activity in the last {selectedTimeRange}</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-50 rounded-lg mb-3">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{analyticsData.trends.recentActivity.reports}</p>
              <p className="text-sm text-gray-600">New Reports</p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-green-50 rounded-lg mb-3">
                <Users className="w-6 h-6 text-green-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{analyticsData.trends.recentActivity.users}</p>
              <p className="text-sm text-gray-600">New Users</p>
            </div>
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-50 rounded-lg mb-3">
                <Briefcase className="w-6 h-6 text-purple-600" />
              </div>
              <p className="text-2xl font-bold text-gray-900">{analyticsData.trends.recentActivity.jobs}</p>
              <p className="text-sm text-gray-600">New Jobs</p>
            </div>
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Performance Metrics</h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-900">Report Resolution Rate</p>
                <p className="text-2xl font-bold text-green-600">{analyticsData.trends.reportResolutionRate}%</p>
              </div>
            </div>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-blue-600" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-900">Job Approval Rate</p>
                <p className="text-2xl font-bold text-blue-600">{analyticsData.trends.jobApprovalRate}%</p>
              </div>
            </div>
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-yellow-50 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-yellow-600" />
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-900">Avg. Response Time</p>
                <p className="text-2xl font-bold text-yellow-600">2.4h</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;
